/**
 * Course builder — the admin surface for putting lessons into classes.
 *
 * Sits on the existing /admin/learn page rather than a second screen: that page
 * already curates videos, and splitting "which videos exist" from "where they
 * go" would mean managing content in two places.
 *
 * The whole point is that adding lesson #25 is a form, not a code change.
 */

import { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Plus, Eye, EyeOff, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  adminListCourses,
  adminUpsertCourse,
  adminPlaceLesson,
  adminListExperiences,
  type CourseSummary,
  type CatalogItem,
} from '../../services/learnAPI';

/** Classes 9–10 teach one combined Science; 11–12 split it. */
const SUBJECTS = ['Science', 'Physics', 'Chemistry', 'Biology', 'Mathematics', 'Social Science', 'Excel'];

const slugify = (s: string) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export function CourseBuilder() {
  const [courses, setCourses] = useState<CourseSummary[]>([]);
  const [lessons, setLessons] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const [newTitle, setNewTitle] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, l] = await Promise.all([adminListCourses(), adminListExperiences()]);
      setCourses(c);
      setLessons(l);
    } catch {
      setError('Could not load courses.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const createCourse = async () => {
    const title = newTitle.trim();
    if (!title) return;
    setBusy('new');
    try {
      // "Class 10" → slug class-10, and the grade is read back off the title so
      // ordering by class works without asking for it twice.
      await adminUpsertCourse({ slug: slugify(title), title, gradeLevel: title, isPublished: true });
      setNewTitle('');
      await load();
    } catch {
      setError('Could not create that course.');
    } finally {
      setBusy(null);
    }
  };

  const place = async (lesson: CatalogItem, patch: { courseId?: string | null; chapter?: string | null; order?: number }) => {
    setBusy(lesson.id);
    try {
      await adminPlaceLesson(lesson.id, patch);
      await load();
    } catch {
      setError('Could not move that lesson.');
    } finally {
      setBusy(null);
    }
  };

  const togglePublished = async (course: CourseSummary) => {
    setBusy(course.id);
    try {
      await adminUpsertCourse({ slug: course.slug, isPublished: !course.isPublished });
      await load();
    } catch {
      setError('Could not update that course.');
    } finally {
      setBusy(null);
    }
  };

  const unplaced = lessons.filter((l) => !l.courseId);

  return (
    <Card className="mt-8">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="text-base">Courses</CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            A course is a class. The subject is the section inside it, and each lesson sits in one.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </CardHeader>

      <CardContent className="space-y-8">
        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label htmlFor="new-course">New course</Label>
            <Input
              id="new-course"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder='e.g. "Class 8"'
              onKeyDown={(e) => e.key === 'Enter' && void createCourse()}
            />
          </div>
          <Button onClick={() => void createCourse()} disabled={!newTitle.trim() || busy === 'new'}>
            <Plus className="mr-1.5 h-4 w-4" /> Create
          </Button>
        </div>

        {courses.map((course) => {
          const mine = lessons
            .filter((l) => l.courseId === course.id)
            .sort((a, b) => a.order - b.order);
          return (
            <div key={course.id} className="rounded-lg border p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold">{course.title}</h3>
                  <Badge variant="secondary">{mine.length} lessons</Badge>
                  {!course.isPublished && <Badge variant="outline">Hidden</Badge>}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy === course.id}
                  onClick={() => void togglePublished(course)}
                  title={course.isPublished ? 'Hide from learners' : 'Show to learners'}
                >
                  {course.isPublished ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </Button>
              </div>

              <ul className="mt-3 divide-y">
                {mine.map((lesson, i) => (
                  <li key={lesson.id} className="flex items-center gap-3 py-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm">{lesson.title ?? 'Untitled'}</p>
                      <p className="text-xs text-muted-foreground">{lesson.chapter ?? 'no subject'}</p>
                    </div>

                    <select
                      value={lesson.chapter ?? ''}
                      disabled={busy === lesson.id}
                      onChange={(e) => void place(lesson, { chapter: e.target.value || null })}
                      className="h-8 rounded-md border bg-background px-2 text-xs"
                    >
                      <option value="">— subject —</option>
                      {SUBJECTS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>

                    {/* Swap with the neighbour: two writes, and the list cannot
                        end up half-renumbered the way a drag mid-flight can. */}
                    <Button
                      variant="ghost" size="sm" disabled={i === 0 || busy === lesson.id}
                      onClick={() => void place(lesson, { order: lesson.order - 1 })}
                    >
                      <ArrowUp className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost" size="sm" disabled={i === mine.length - 1 || busy === lesson.id}
                      onClick={() => void place(lesson, { order: lesson.order + 1 })}
                    >
                      <ArrowDown className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost" size="sm" disabled={busy === lesson.id}
                      onClick={() => void place(lesson, { courseId: null })}
                      title="Remove from this course"
                    >
                      ×
                    </Button>
                  </li>
                ))}
                {mine.length === 0 && (
                  <li className="py-3 text-sm text-muted-foreground">
                    No lessons yet — add one from the list below.
                  </li>
                )}
              </ul>
            </div>
          );
        })}

        {unplaced.length > 0 && (
          <div>
            <h3 className="font-semibold">Not in a course yet</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              These are published and browsable; they simply have no class.
            </p>
            <ul className="mt-3 divide-y">
              {unplaced.map((lesson) => (
                <li key={lesson.id} className="flex items-center gap-3 py-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm">{lesson.title ?? 'Untitled'}</p>
                    <p className="text-xs text-muted-foreground">
                      {[lesson.grade, lesson.subject].filter(Boolean).join(' · ') || 'no metadata'}
                    </p>
                  </div>
                  <select
                    defaultValue=""
                    disabled={busy === lesson.id}
                    onChange={(e) =>
                      e.target.value &&
                      void place(lesson, { courseId: e.target.value, chapter: lesson.subject ?? null })
                    }
                    className="h-8 rounded-md border bg-background px-2 text-xs"
                  >
                    <option value="">Add to course…</option>
                    {courses.map((c) => (
                      <option key={c.id} value={c.id}>{c.title}</option>
                    ))}
                  </select>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
